//changed due to package upgrade
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppTextField from '@projectK/components/AppTextField';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form } from 'formik';
import React, { useEffect, useState, useRef } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useDropzone } from 'react-dropzone';
import 'react-quill-new/dist/quill.snow.css';
import { CameraAltRounded } from '@mui/icons-material';
import productThemes from '@projectK/constants/ProductThemes';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import PropTypes from 'prop-types';
import {
  useRolePermissions,
  hasPermission,
} from '@projectK/context/RolesProvider';
import IntlMessages from '@projectK/helpers/IntlMessages';

const getButtonBackgroundColor = (companyTheme, isMargin) => ({
  ...(isMargin && { mt: 6 }), // Conditionally add mt only if isMargin is true
  backgroundColor: companyTheme ? companyTheme.primary.main : '#2196f3',
  '&:hover': {
    backgroundColor: companyTheme
      ? alpha(companyTheme.primary.main, 0.8)
      : alpha('#2196f3', 0.8),
  },
});
const getTextColor = (companyTheme) => ({
  color: companyTheme ? companyTheme.primary.main : '#2196f3',
});
const getBorderColor = (companyTheme) => ({
  borderColor: companyTheme ? companyTheme.primary.main : '#2196f3',
  '&:hover': {
    borderColor: companyTheme
      ? alpha(companyTheme.primary.main, 0.8)
      : alpha('#2196f3', 0.8),
  },
  color: companyTheme ? companyTheme.primary.main : '#2196f3',
});
const errorElements = (touched, uploadedFiles, errors) => {
  let errorElement = null; // Initialize to null

  if (touched.image && !uploadedFiles.length) {
    errorElement = (
      <div
        className='error'
        style={{
          color: '#d32f2f',
          fontSize: '13px',
          marginLeft: '13px',
          marginTop: '6px',
        }}
      >
        {errors.image}
      </div>
    );
  } else {
    delete errors.image;
  }

  return errorElement; // Return the JSX element or null
};
const timeZones = [
  { id: 1, name: 'America/New_York' }, // Eastern Time
  { id: 2, name: 'America/Chicago' }, // Central Time
  { id: 3, name: 'America/Denver' }, // Mountain Time
  { id: 4, name: 'America/Los_Angeles' }, // Pacific Time
  { id: 5, name: 'America/Anchorage' }, // Alaska Time
  { id: 6, name: 'Pacific/Honolulu' }, // Hawaii Time
  { id: 7, name: 'America/Phoenix' }, // Arizona (no DST)
  { id: 8, name: 'America/Sitka' }, // Alaska (with DST)
  { id: 9, name: 'America/Adak' }, // Aleutian Islands
  { id: 10, name: 'America/Puerto_Rico' }, // Atlantic Time (PR)
  { id: 11, name: 'America/Indiana/Indianapolis' }, // Eastern Time (Indiana)
  { id: 12, name: 'America/Indiana/Knox' }, // Central Time (Indiana)
  { id: 13, name: 'America/Indiana/Tell_City' },
  { id: 14, name: 'America/Detroit' }, // Eastern Time (Michigan)
  { id: 15, name: 'America/Regina' }, // Central (no DST, Canada)
  { id: 16, name: 'America/Toronto' }, // Eastern Time (Canada)
  { id: 17, name: 'America/Winnipeg' }, // Central Time (Canada)
  { id: 18, name: 'America/Vancouver' }, // Pacific Time (Canada)
  { id: 19, name: 'America/Edmonton' }, // Mountain Time (Canada)
  { id: 20, name: 'America/Halifax' }, // Atlantic Time (Canada)
  { id: 21, name: 'America/St_Johns' }, // Newfoundland Time
  { id: 22, name: 'America/Boise' }, // Mountain Time (Idaho)
  { id: 23, name: 'America/Nome' }, // Alaska Time
  { id: 24, name: 'America/Juneau' }, // Alaska
  { id: 25, name: 'America/Whitehorse' }, // Yukon
  { id: 26, name: 'America/Yellowknife' }, // Northwest Territories
  { id: 27, name: 'America/Montreal' }, // Eastern Canada
  { id: 28, name: 'America/Mexico_City' }, // Central Time (Mexico)
  { id: 29, name: 'America/Monterrey' }, // Northern Mexico
  { id: 30, name: 'America/Tijuana' }, // Pacific Time (Mexico)
];
const GeneralForm = ({
  settings,
  formikProps,
  handleImageUpload,
  handleBannerUpload,
  handleBlur,
  setFieldValue,
  touched,
  errors,
  resetForm,
  values,
  isEdit,
  setIsEdit,
  handleMobileFormat,
  setErrors,
}) => {
  const { rolePermissions } = useRolePermissions();
  const companyTheme = CompanyTheme();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const editorRef = useRef(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (editorRef.current) {
      editorRef?.current?.getContent(); // Get content when needed
    }
  }, []);

  const dropzone = useDropzone({
    accept: {
      'image/png': ['.png', '.jpeg', '.jpg'],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      setUploadedFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }),
        ),
      );

      // Call handleImageUpload here to upload the selected file
      if (acceptedFiles.length > 0) {
        setLoading(true);
        handleImageUpload(acceptedFiles[0], formikProps)
          ?.then(() => setLoading(false))
          ?.catch(() => setLoading(false));
      }
    },
  });
  const [programThemes, setProgramThemes] = React.useState(settings.theme);

  let errorElement = errorElements(touched, uploadedFiles, errors);
  return (
    <Form noValidate autoComplete='off'>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant='h4' sx={(theme) => getTextColor(companyTheme)}>
            General Settings
          </Typography>
          <Typography variant='body1' sx={{ mt: 2, color: 'text.secondary' }}>
            Manage your general account settings
          </Typography>
        </Box>
        <Box>
          {!isEdit &&
            hasPermission(
              rolePermissions,
              'Company Settings - General',
              'update',
            ) && (
              <EditIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => setIsEdit(true)}
              />
            )}
        </Box>
      </Box>
      <Divider sx={{ my: 4 }} />
      <AppGridContainer>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box>
            <Typography variant='h5' sx={(theme) => getTextColor(companyTheme)}>
              Account Info
            </Typography>
            <Typography variant='body1' sx={{ mt: 1, color: 'text.secondary' }}>
              Update your account details.
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box
            sx={{
              border: '1px solid #EAECF0',
              p: 6,
              borderRadius: 3,
              boxShadow:
                '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
            }}
          >
            <AppGridContainer>
              <Grid size={{ xs: 12, md: 6 }}>
                <AppTextField
                  name='name'
                  variant='outlined'
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Name'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                  inputProps={{ maxLength: 101 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <AppTextField
                  name='owner_name'
                  variant='outlined'
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Owner Name'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                  inputProps={{ maxLength: 101 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <AppTextField
                  name='email'
                  variant='outlined'
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Email'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name='phone_number'
                  variant='outlined'
                  inputProps={{ maxLength: 12 }}
                  value={values.phone_number}
                  error={!!errors.phone_number}
                  helperText={errors.phone_number}
                  onChange={(event) => {
                    setFieldValue(
                      handleMobileFormat(event, 'phone_number').name,
                      handleMobileFormat(event, 'phone_number').value,
                    );
                  }}
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Phone Number'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <AppTextField
                  name='site_contact_email'
                  variant='outlined'
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Site Contact Email'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name='site_contact_phone_number'
                  variant='outlined'
                  value={values.site_contact_phone_number}
                  inputProps={{ maxLength: 12 }}
                  error={!!errors.site_contact_phone_number}
                  helperText={errors.site_contact_phone_number}
                  onChange={(event) => {
                    setFieldValue(
                      handleMobileFormat(event, 'site_contact_phone_number')
                        .name,
                      handleMobileFormat(event, 'site_contact_phone_number')
                        .value,
                    );
                  }}
                  sx={{
                    width: '100%',
                    my: 2,
                  }}
                  label='Site Contact Phone Number'
                  slotProps={{
                    input: {
                      readOnly: !isEdit,
                    },
                  }}
                />
              </Grid>
            </AppGridContainer>
          </Box>
        </Grid>
      </AppGridContainer>
      <Divider sx={{ my: 4 }} />
      <AppGridContainer>
        <Grid size={{ xs: 12, md: 3 }}>
          <Box>
            <Typography variant='h5' sx={(theme) => getTextColor(companyTheme)}>
              Basic Settings
            </Typography>
            <Typography variant='body1' sx={{ mt: 1, color: 'text.secondary' }}>
              These settings control what displays on your company home page.
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Box
            sx={{
              border: '1px solid #EAECF0',
              p: 6,
              borderRadius: 3,
              boxShadow:
                '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
            }}
          >
            <AppGridContainer>
              <Grid size={{ xs: 12, md: 6 }}>
                {isEdit ? (
                  <TextField
                    sx={{
                      width: '100%',
                    }}
                    select
                    name='theme'
                    value={programThemes}
                    label='Theme'
                    onChange={(event) => {
                      setProgramThemes(event.target.value);
                      setFieldValue('theme', event.target.value); // Update the form field value
                    }}
                  >
                    <MenuItem value={'None'}>None</MenuItem>
                    {productThemes.map((theme) => (
                      <MenuItem key={theme.title} value={theme.title}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: theme.primary.main,
                              marginRight: '10px',
                            }}
                          ></div>
                          {theme.title}
                        </div>
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <AppTextField
                    name='theme'
                    variant='outlined'
                    sx={{
                      width: '100%',
                      my: 2,
                    }}
                    label='Theme'
                    slotProps={{
                      input: {
                        readOnly: !isEdit,
                      },
                    }}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 12, md: 12 }}>
                <Typography variant='h4' sx={{ mb: 4 }}>
                  Logo:
                </Typography>
                {isEdit && (
                  <Typography sx={{ mt: '5px' }}>
                    <span style={{ fontWeight: 'bold' }}>Note:</span>
                    Image dimensions are {`760 × 500`}
                  </Typography>
                )}
                <Box
                  className='dropzone-img-200'
                  sx={{
                    border: '1px solid #EAECF0',
                    p: 6,
                    borderRadius: 3,
                    boxShadow:
                      '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                  }}
                >
                  {isEdit ? (
                    <Box
                      sx={{ cursor: 'pointer', width: 'fit-content' }}
                      {...dropzone.getRootProps({ className: 'dropzone' })}
                    >
                      {(uploadedFiles?.[0]?.preview ||
                        settings.home_logo_url) && (
                        <img
                          src={
                            uploadedFiles?.[0]?.preview ||
                            `${process.env.NX_CLOUD_FRONT_URL}/${settings?.home_logo_url}`
                          }
                          alt='logo'
                          style={{ width: 200, height: 100 }}
                        />
                      )}

                      <Box>
                        <label htmlFor='image-upload-input'>
                          <input {...dropzone.getInputProps()} />
                          <Button
                            component='span'
                            variant='contained'
                            color='primary'
                            sx={(theme) =>
                              getButtonBackgroundColor(companyTheme, true)
                            }
                          >
                            <CameraAltRounded />
                            Add/Upload Image
                          </Button>
                        </label>
                        {errorElement}
                      </Box>
                    </Box>
                  ) : (
                    settings?.home_logo_url && (
                      <img
                        src={`${process.env.NX_CLOUD_FRONT_URL}/${settings.home_logo_url}`}
                        alt='logo'
                      />
                    )
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  options={timeZones}
                  getOptionLabel={(option) => option.name}
                  value={
                    timeZones.find((tz) => tz.name === values.timezone) || null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue('timezone', newValue ? newValue.name : null);
                    touched.timezone = true;
                  }}
                  readOnly={!isEdit}
                  disableClearable={!isEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name='timezone'
                      label='Timezone'
                      variant='outlined'
                      error={touched.timezone && Boolean(errors.timezone)}
                      helperText={touched.timezone && errors.timezone}
                      InputProps={{// NOSONAR - MUI v6 Autocomplete still requires InputProps for dropdown anchor
                      ...params.InputProps,
                      type: 'search',
                    }}
                    />
                  )}
                />
              </Grid>
            </AppGridContainer>
          </Box>
        </Grid>
      </AppGridContainer>
      {isEdit && (
        <Stack
          direction='row'
          justifyContent='flex-end'
          spacing={5}
          sx={{ mt: 3 }}
        >
          <Button
            getBorderColor
            sx={(theme) => getBorderColor(companyTheme)}
            onClick={() => {
              setIsEdit(false);
              setErrors({});
              resetForm();
            }}
          >
            <IntlMessages id='common.cancel' />
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            sx={(theme) => getButtonBackgroundColor(companyTheme, false)}
            disabled={loading}
          >
            <IntlMessages id='common.save' />
          </Button>
        </Stack>
      )}
    </Form>
  );
};

GeneralForm.propTypes = {
  settings: PropTypes.object,
  formikProps: PropTypes.object.isRequired, // Ensure Formik props are passed
  handleImageUpload: PropTypes.func,
  handleBannerUpload: PropTypes.func,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
  resetForm: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  isEdit: PropTypes.bool.isRequired,
  setIsEdit: PropTypes.func.isRequired,
  handleMobileFormat: PropTypes.func,
  setErrors: PropTypes.func,
};
export default GeneralForm;
